import React, { useMemo } from 'react';

import { useField } from 'formik';
import { Box, FormHelperText, FormLabel } from '@mui/material';

import { BaseTextField, BaseTextFieldProps } from '../BaseTextField';
import { NumericFormat } from 'react-number-format';


export type HeightInputProps = {
  name: string;
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  color?: BaseTextFieldProps['color'];
  disabled?: boolean;
};

export const getFeetAndInchesFromHeightInches = (totalInches: number | undefined | null) => {
  let feet;
  let inches;

  if (typeof totalInches === 'number') {
    feet = (totalInches - totalInches % 12) / 12;
    inches = totalInches % 12;
  }

  return { feet, inches };
};

export const HeightInput: React.FC<HeightInputProps> = ({ name, label, ...props }) => {
  const [ focused, setFocused ] = React.useState(false);
  const [ field, meta, helpers ] = useField<number | null>({
    name: name,
  });

  const error = Boolean(meta.touched && meta.error);
  const helperText = (error && meta.error) || props.helperText;

  const { feet = '', inches = '' } = useMemo(() => getFeetAndInchesFromHeightInches(field.value), [ field.value ]);
  const feetMaxValue = 10;
  const inchesMaxValue = 12;

  return (
    <Box>
      {label && (
        <FormLabel error={error} htmlFor={name} focused={focused} color={props.color} style={{ lineHeight: '24px' }}>
          {label}
        </FormLabel>
      )}
      <Box display="flex" gap="10px">
        <NumericFormat
          error={error}
          customInput={BaseTextField}
          decimalScale={0}
          isAllowed={(values) => {
            if (values.floatValue === undefined) {
              return true;
            }
            if (typeof values.floatValue === 'number') {
              if (feet === 0 && values.floatValue >= feetMaxValue) {
                return true;
              }
              return values.floatValue < feetMaxValue;
            }

            return false;
          }}

          id={name + '-ft'}
          color={props.color}
          value={feet.toString()}
          onBlur={() => {
            helpers.setTouched(true);
            setFocused(false);
          }}
          onFocus={() => setFocused(true)}
          onValueChange={(values) => {
            const newValue = values.floatValue;

            if (typeof newValue === 'number') {
              const shouldReplace = feet === 0 && newValue / feetMaxValue >= 1;
              const feetValue = shouldReplace ? newValue / feetMaxValue : newValue;

              helpers.setValue(feetValue * 12 + Number(inches));
            } else {
              helpers.setValue(Number(inches));
            }
          }}
          slotProps={{
            input: {
              endAdornment: 'ft',
            }
          }}
          {...props}
        />

        <NumericFormat
          error={error}
          customInput={BaseTextField}
          decimalScale={0}
          isAllowed={(values) => {
            if (values.floatValue === undefined) {
              return true;
            }
            if (typeof values.floatValue === 'number') {
              if (inches === 0 && values.floatValue >= inchesMaxValue) {
                return true;
              }
              return values.floatValue < inchesMaxValue;
            }

            return false;
          }}

          id={name + '-in'}
          value={inches.toString()}
          color={props.color}
          onBlur={() => {
            helpers.setTouched(true);
            setFocused(false);
          }}
          onFocus={() => setFocused(true)}
          onValueChange={(values) => {
            const newValue = values.floatValue;

            if (typeof newValue === 'number') {
              const shouldReplace = inches === 0 && newValue / inchesMaxValue >= 1;
              const inchesValue = shouldReplace ? newValue / feetMaxValue : newValue;

              helpers.setValue(Number(feet) * 12 + inchesValue);
            } else {
              helpers.setValue(Number(feet) * 12);
            }
          }}
          slotProps={{
            input: {
              endAdornment: 'in',
            }
          }}
          {...props}
        />
      </Box>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </Box>
  );
};
